import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { Loader2, Link2, Unlink, RefreshCw, Bike, Activity, Clock, TrendingUp, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('cardioai_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const StravaPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activities, setActivities] = useState([]);
  const [stravaConnected, setStravaConnected] = useState(false);

  useEffect(() => {
    // Check URL params for Strava callback
    const urlParams = new URLSearchParams(window.location.search);
    const stravaStatus = urlParams.get('strava');
    if (stravaStatus === 'connected') {
      toast.success('Strava conectado com sucesso!');
      window.history.replaceState({}, document.title, '/strava');
    } else if (stravaStatus === 'error') {
      toast.error('Erro ao conectar com Strava');
      window.history.replaceState({}, document.title, '/strava');
    }
    
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Check if Strava is connected
      const meResponse = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      setStravaConnected(meResponse.data.strava_connected);
      
      if (meResponse.data.strava_connected) {
        const activitiesResponse = await axios.get(`${API}/strava/activities`, { headers: getAuthHeaders() });
        setActivities(activitiesResponse.data);
      }
    } catch (error) {
      console.error('Error fetching Strava data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await axios.get(`${API}/strava/auth-url`, { headers: getAuthHeaders() });
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Error getting Strava auth URL:', error);
      if (error.response?.status === 400) {
        toast.error('Strava não configurado. Entre em contato com o suporte.');
      } else {
        toast.error('Erro ao conectar com Strava');
      }
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.delete(`${API}/strava/disconnect`, { headers: getAuthHeaders() });
      setStravaConnected(false);
      setActivities([]);
      toast.success('Strava desconectado');
    } catch (error) {
      toast.error('Erro ao desconectar Strava');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/strava/sync`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      
      // Refresh activities
      const activitiesResponse = await axios.get(`${API}/strava/activities`, { headers: getAuthHeaders() });
      setActivities(activitiesResponse.data);
    } catch (error) {
      toast.error('Erro ao sincronizar atividades');
    } finally {
      setSyncing(false);
    }
  };

  const formatDistance = (meters) => {
    return (meters / 1000).toFixed(2) + ' km';
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getActivityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'ride':
      case 'virtualride':
        return <Bike className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="strava-page">
        <div className="mb-8">
          <h1 
            className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-2"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Integração <span className="text-[#FC4C02]">Strava</span>
          </h1>
          <p className="text-gray-400">
            Liga a tua conta Strava para importar atividades e personalizar os teus planos
          </p>
        </div>

        {/* Connection Status Card */}
        <Card className={`mb-8 ${stravaConnected ? 'bg-[#FC4C02]/10 border-[#FC4C02]/30' : 'bg-[#121212] border-white/10'}`}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${stravaConnected ? 'strava-connected' : 'bg-gray-800'}`}>
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                    {stravaConnected ? 'Strava Conectado' : 'Conectar com Strava'}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {stravaConnected 
                      ? 'As tuas atividades estão a ser sincronizadas'
                      : 'Importa as tuas corridas, pedaladas e mais'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                {stravaConnected ? (
                  <>
                    <Button 
                      onClick={handleSync}
                      disabled={syncing}
                      className="bg-[#FC4C02] hover:bg-[#E34402] text-white"
                      data-testid="sync-strava-btn"
                    >
                      {syncing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Sincronizar
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleDisconnect}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid="disconnect-strava-btn"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      Desconectar
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={handleConnect}
                    className="bg-[#FC4C02] hover:bg-[#E34402] text-white px-8"
                    data-testid="connect-strava-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Conectar com Strava
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activities */}
        {stravaConnected && (
          <>
            {/* Stats */}
            {activities.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#32CD32]/10 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-[#32CD32]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{activities.length}</p>
                        <p className="text-xs text-gray-500">Atividades</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#32CD32]/10 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-[#32CD32]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">
                          {formatDistance(activities.reduce((sum, a) => sum + (a.distance || 0), 0))}
                        </p>
                        <p className="text-xs text-gray-500">Distância Total</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#32CD32]/10 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-[#32CD32]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">
                          {formatDuration(activities.reduce((sum, a) => sum + (a.duration || 0), 0))}
                        </p>
                        <p className="text-xs text-gray-500">Tempo Total</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#FC4C02]/10 flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#FC4C02]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#FC4C02]">
                          {activities.filter(a => a.average_heartrate).length > 0 
                            ? Math.round(activities.filter(a => a.average_heartrate).reduce((sum, a) => sum + a.average_heartrate, 0) / activities.filter(a => a.average_heartrate).length)
                            : '-'
                          }
                        </p>
                        <p className="text-xs text-gray-500">FC Média</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Activities List */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-lg uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  Atividades Recentes
                </CardTitle>
                <CardDescription>As tuas últimas atividades importadas do Strava</CardDescription>
              </CardHeader>
              <CardContent>
                {activities.length === 0 ? (
                  <div className="text-center py-12">
                    <Bike className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Nenhuma atividade encontrada</p>
                    <Button onClick={handleSync} className="btn-primary" disabled={syncing}>
                      {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Sincronizar Agora
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activities.slice(0, 20).map((activity) => (
                      <div 
                        key={activity.id}
                        className="p-4 rounded-xl bg-black/30 border border-white/5 hover:border-[#FC4C02]/30 transition-colors"
                        data-testid={`activity-${activity.strava_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#FC4C02]/10 flex items-center justify-center text-[#FC4C02]">
                              {getActivityIcon(activity.activity_type)}
                            </div>
                            <div>
                              <p className="font-medium">{activity.name}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>{activity.activity_type}</span>
                                <span>{formatDate(activity.start_date)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div className="text-right">
                              <p className="font-mono font-medium">{formatDistance(activity.distance)}</p>
                              <p className="text-gray-500">Distância</p>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-medium">{formatDuration(activity.duration)}</p>
                              <p className="text-gray-500">Duração</p>
                            </div>
                            {activity.average_heartrate && (
                              <div className="text-right">
                                <p className="font-mono font-medium text-[#FC4C02]">{Math.round(activity.average_heartrate)}</p>
                                <p className="text-gray-500">FC</p>
                              </div>
                            )}
                            <a 
                              href={`https://www.strava.com/activities/${activity.strava_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#FC4C02] hover:text-[#FC4C02]/80"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Not connected info */}
        {!stravaConnected && (
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="py-16 text-center">
              <svg className="w-20 h-20 text-[#FC4C02] mx-auto mb-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
              </svg>
              <h3 className="text-2xl font-bold mb-4 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                Liga o teu Strava
              </h3>
              <p className="text-gray-400 max-w-md mx-auto mb-8">
                Ao ligar a tua conta Strava, os teus planos de treino serão personalizados com base no teu histórico de atividades, incluindo distâncias, tempos e frequência cardíaca.
              </p>
              <ul className="text-left max-w-md mx-auto space-y-3 mb-8">
                {[
                  'Importa corridas, pedaladas e natação',
                  'Planos baseados no teu histórico real',
                  'Análise de frequência cardíaca e ritmo',
                  'Sincronização automática de atividades'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 rounded-full bg-[#FC4C02]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={handleConnect}
                className="bg-[#FC4C02] hover:bg-[#E34402] text-white px-10 py-6 text-lg rounded-full"
              >
                <Link2 className="w-5 h-5 mr-2" />
                Conectar com Strava
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StravaPage;
